/**
 * Projects — list page rendered 1:1 in the design-folder style.
 *
 * HTML structure verbatim from
 *   /Users/gudupuramesh/Documents/design ui/anti Interior io ui web/projects.html
 * Real data substituted in: `useProjects()` for the rows,
 * `useProjectTotals()` for spent % per row.
 *
 * Class names used (all defined in iio.css):
 *   .projects-header / .projects-title-row / .projects-stats
 *   .filter-bar / .filter-chip / .filter-sep / .filter-select
 *   .view-toggle / .view-btn
 *   .filter-search-wrap / .filter-search
 *   .proj-table-wrap / .proj-table
 *   .proj-name-cell / .proj-thumb / .proj-name-text / .proj-name-title / .proj-name-client
 *   .stage-pill .stage-design|approval|execution|handover|completed
 *   .owner-cell / .owner-avatar
 *   .spent-cell / .spent-bar / .spent-bar-fill .low|mid|high / .spent-pct
 *   .milestone-date / .milestone-title
 *   .health-wrap / .health-dot .green|amber|red / .tip
 *   .updated-text / .row-menu-btn
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { canManageProjects } from '@shared/perms';

import { NewProjectModal } from '@/components/NewProjectModal';
import {
  PROJECT_PHASES,
  phaseLabel,
  updateProject,
  type ProjectPhase,
} from '@/lib/projects';
import { useProjects, type ProjectListItem } from '@/lib/useProjects';
import { useProjectTotals } from '@/lib/useProjectTotals';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';

// ───── Stage mapping (mobile status → iio.css stage-pill modifier) ─────

type StageKey = 'design' | 'approval' | 'execution' | 'handover' | 'completed';

function stageFor(status: ProjectListItem['status'], progress: number): StageKey {
  if (status === 'completed') return 'completed';
  // No mobile field for "handover" — map progress >=90% to handover for visual richness.
  if (progress >= 90) return 'handover';
  if (status === 'on_hold') return 'approval'; // amber pill matches "needs attention"
  if (progress >= 30) return 'execution';
  return 'design';
}

// `stageLabel` removed — the Phase column now reads its label from
// `phaseLabel()` in lib/projects. The `StageKey` colour mapping is
// still used by `phaseToStageClass()` below.

// ───── Thumb colour palette (seeded by project name) ─────

const THUMB_COLORS: { bg: string; fg: string }[] = [
  { bg: '#DBEAFE', fg: '#2563EB' }, // blue
  { bg: '#FCE7F3', fg: '#DB2777' }, // pink
  { bg: '#D1FAE5', fg: '#059669' }, // green
  { bg: '#FEF3C7', fg: '#B45309' }, // amber
  { bg: '#E0E7FF', fg: '#4F46E5' }, // indigo
  { bg: '#FFE4E6', fg: '#E11D48' }, // rose
];

function pickThumb(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return THUMB_COLORS[Math.abs(h) % THUMB_COLORS.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// ───── Currency + date formatting (matches iio.css `.tn` cells) ─────

function formatInrFull(n: number | undefined | null): string {
  if (!n || n <= 0) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

function relTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function nextMilestone(p: ProjectListItem): { date: string; title: string } {
  const endMs = p.endDate?.toMillis() ?? null;
  if (!endMs) return { date: '—', title: 'No milestone set' };
  const d = new Date(endMs);
  const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  return { date, title: 'Project deadline' };
}

/** Margin-bar tone — inverse of the old spent bar. Higher margin %
 *  is healthier, so high → low/green CSS class (same colours, new
 *  semantic). low | mid | high refer to the iio.css fill colours
 *  (green | amber | red) — kept as-is so we don't need new CSS. */
function marginBarTone(pct: number): 'low' | 'mid' | 'high' {
  if (pct >= 30) return 'low';   // green — healthy margin
  if (pct >= 10) return 'mid';   // amber — thin margin
  return 'high';                  // red   — negative or near-zero
}

function healthFor(p: ProjectListItem): { dot: 'green' | 'amber' | 'red'; tip: string } {
  const endMs = p.endDate?.toMillis() ?? null;
  const progress = typeof p.progress === 'number' ? p.progress : 0;
  if (p.status === 'completed') return { dot: 'green', tip: 'Completed' };
  if (p.status === 'on_hold')   return { dot: 'amber', tip: 'On hold' };
  if (endMs && endMs < Date.now() && progress < 100) {
    return { dot: 'red', tip: 'Past deadline' };
  }
  if (endMs && endMs - Date.now() < 7 * 86400000 && progress < 80) {
    return { dot: 'amber', tip: 'Slightly delayed' };
  }
  return { dot: 'green', tip: 'On track' };
}

// ───── Filters ─────

type StatusFilter = 'all' | 'active' | 'on_hold' | 'completed' | 'archived';
type Sort = 'updated' | 'name' | 'value' | 'deadline';

function sortRows(rows: ProjectListItem[], sort: Sort): ProjectListItem[] {
  const arr = [...rows];
  switch (sort) {
    case 'name':
      return arr.sort((a, b) => a.name.localeCompare(b.name));
    case 'value':
      return arr.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    case 'deadline':
      return arr.sort(
        (a, b) =>
          (a.endDate?.toMillis() ?? Number.POSITIVE_INFINITY) -
          (b.endDate?.toMillis() ?? Number.POSITIVE_INFINITY),
      );
    default:
      return arr; // already sorted by createdAt desc inside useProjects
  }
}

// ───── Page ─────

export function ProjectsPage() {
  const { projects, loading, error, orgId } = useProjects();
  const { totalsByProject } = useProjectTotals(orgId);
  const { role: orgRole } = useCurrentOrgRole();
  // Phase change is gated server-side by `canManageProjects` (SA /
  // Admin / Manager who is a project member). Hide the dropdown
  // for everyone else so the UI doesn't promise an action that
  // will fail at the rule layer.
  const canEditPhase = canManageProjects(orgRole);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<Sort>('updated');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  const counts = useMemo(() => {
    const c = { total: 0, active: 0, on_hold: 0, completed: 0, archived: 0, completedThisMonth: 0 };
    const month = new Date();
    month.setDate(1);
    month.setHours(0, 0, 0, 0);
    const monthStart = month.getTime();
    for (const p of projects) {
      c.total += 1;
      const s = p.status ?? 'active';
      if (s === 'active') c.active += 1;
      else if (s === 'on_hold') c.on_hold += 1;
      else if (s === 'completed') {
        c.completed += 1;
        const updMs = p.createdAt?.toMillis?.() ?? 0;
        if (updMs >= monthStart) c.completedThisMonth += 1;
      }
      else if (s === 'archived') c.archived += 1;
    }
    return c;
  }, [projects]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = projects.filter((p) => {
      if (status !== 'all') {
        const s = p.status ?? 'active';
        if (s !== status) return false;
      }
      if (q) {
        const blob = [p.name, p.client ?? '', p.location ?? '', p.siteAddress ?? '']
          .join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
    return sortRows(base, sort);
  }, [projects, status, search, sort]);

  return (
    <div className="page active" id="page-projects" style={{ display: 'block' }}>
      {/* Header */}
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Projects</h1>
          <div className="projects-stats">
            <span>{counts.active}</span> active ·{' '}
            <span>{counts.completedThisMonth}</span> completed this month
          </div>
        </div>
        {canManageProjects(orgRole) ? (
          // Hidden for roles that can't write — Firestore rule blocks it anyway
          <button
            className="btn-primary"
            type="button"
            onClick={() => setCreateOpen(true)}
            disabled={!orgId}
          >
            <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> New project
          </button>
        ) : null}
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        {([
          ['all',       'All',       counts.total],
          ['active',    'Active',    counts.active],
          ['on_hold',   'On hold',   counts.on_hold],
          ['completed', 'Completed', counts.completed],
          ['archived',  'Archived',  counts.archived],
        ] as [StatusFilter, string, number][]).map(([key, label, n]) => (
          <button
            key={key}
            type="button"
            className={`filter-chip${status === key ? ' active' : ''}`}
            onClick={() => setStatus(key)}
          >
            {label} {n > 0 ? <span style={{ opacity: 0.7 }}>· {n}</span> : null}
          </button>
        ))}

        <div className="filter-sep" />

        <select
          className="filter-select"
          value={sort}
          onChange={(e) => setSort(e.currentTarget.value as Sort)}
        >
          <option value="updated">Recently updated</option>
          <option value="name">Name A–Z</option>
          <option value="value">Value high–low</option>
          <option value="deadline">Deadline soonest</option>
        </select>

        <div className="view-toggle">
          <button className="view-btn active" type="button">
            <i className="ph ph-list" /> Table
          </button>
          <button className="view-btn" type="button" disabled>
            <i className="ph ph-kanban" /> Kanban
          </button>
        </div>

        <div className="filter-search-wrap">
          <i className="ph ph-magnifying-glass" />
          <input
            className="filter-search"
            type="text"
            placeholder="Search projects…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          Loading projects…
        </div>
      ) : error ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--critical)' }}>
          Couldn't load projects: {error}
        </div>
      ) : projects.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>
            <i className="ph ph-folder-simple" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            No projects yet
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Create your first project to start tracking tasks, payments, materials, and site reports.
          </div>
          {canManageProjects(orgRole) ? (
            <button className="btn-primary" type="button" onClick={() => setCreateOpen(true)}>
              <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> Create your first project
            </button>
          ) : null}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 14 }}>No projects match your filters.</div>
          <button
            className="btn-secondary"
            type="button"
            style={{ marginTop: 12 }}
            onClick={() => { setStatus('all'); setSearch(''); }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="proj-table-wrap">
          <table className="proj-table">
            <thead>
              <tr>
                <th style={{ width: 260 }}>Project</th>
                <th>Stage</th>
                <th>Owner</th>
                <th className="right">Value</th>
                <th>P.Margin</th>
                <th>Next milestone</th>
                <th className="center">Health</th>
                <th>Updated</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const thumb = pickThumb(p.name);
                const progress = typeof p.progress === 'number' ? p.progress : 0;
                const stage = stageFor(p.status, progress);
                const value = p.value ?? 0;
                const totals = totalsByProject.get(p.id);
                const spent = totals?.expense ?? 0;
                // Margin = planned budget − spent. Positive ₹ =
                // studio's gross profit; negative = over-budget loss.
                const margin = value - spent;
                const marginPct = value > 0 ? Math.round((margin / value) * 100) : 0;
                const tone = marginBarTone(marginPct);
                const milestone = nextMilestone(p);
                const health = healthFor(p);
                const updated = p.createdAt?.toMillis?.() ?? null;
                const subtitle = [p.client, p.location ?? p.siteAddress].filter(Boolean).join(' · ');

                return (
                  <tr
                    key={p.id}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button')) return;
                      navigate(`/projects/${p.id}`);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="proj-name-cell">
                        <div
                          className="proj-thumb"
                          style={{ background: thumb.bg, color: thumb.fg }}
                        >
                          {initials(p.name)}
                        </div>
                        <div className="proj-name-text">
                          <div className="proj-name-title">{p.name}</div>
                          <div className="proj-name-client">{subtitle || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {/* Phase chip — clickable native <select>
                          overlaid invisibly on the chip so the
                          dropdown opens on click. Falls back to the
                          derived `stage` colour mapping when the
                          project doc has no explicit phase set
                          (legacy docs) so the visual stays familiar.
                          stopPropagation keeps the row click from
                          firing through and navigating away. */}
                      <PhaseCell
                        projectId={p.id}
                        phase={p.phase ?? null}
                        fallbackStage={stage}
                        canEdit={canEditPhase}
                      />
                    </td>
                    <td>
                      {p.client ? (
                        <div className="owner-cell">
                          <div
                            className="owner-avatar"
                            style={{ background: thumb.bg, color: thumb.fg }}
                          >
                            {initials(p.client)}
                          </div>
                          {p.client.split(/\s+/)[0]}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                      )}
                    </td>
                    <td className="right tn">{formatInrFull(value)}</td>
                    <td>
                      {/* P.Margin cell — gross profit margin as
                          a % of the planned budget. Bar fills with
                          the % of budget still profit; negative
                          margin collapses the bar to zero and the
                          number paints red. */}
                      {value > 0 ? (
                        <div className="spent-cell">
                          <div className="spent-bar">
                            <div
                              className={`spent-bar-fill ${tone}`}
                              style={{
                                width: `${Math.max(0, Math.min(100, marginPct))}%`,
                              }}
                            />
                          </div>
                          <span
                            className="spent-pct"
                            style={{
                              color:
                                margin > 0
                                  ? '#059669'
                                  : margin < 0
                                    ? '#dc2626'
                                    : undefined,
                            }}
                          >
                            {marginPct >= 0 ? '+' : '−'}
                            {Math.abs(marginPct)}%
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div className="milestone-date">{milestone.date}</div>
                      <div className="milestone-title">{milestone.title}</div>
                    </td>
                    <td className="center">
                      <div className="health-wrap">
                        <span className={`health-dot ${health.dot}`} />
                        <span className="tip">{health.tip}</span>
                      </div>
                    </td>
                    <td>
                      <span className="updated-text">{relTime(updated)}</span>
                    </td>
                    <td className="center">
                      <button
                        className="row-menu-btn"
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Row actions"
                      >
                        <i className="ph ph-dots-three" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <NewProjectModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        orgId={orgId}
        onCreated={(projectId) => navigate(`/projects/${projectId}`)}
      />
    </div>
  );
}

// ───── Phase cell ──────────────────────────────────────────
//
// Renders the phase chip + invisible native <select> that opens
// the dropdown. When the project has no `phase` set we still show
// a chip ("Set phase") so the user can make a choice; we colour-
// match to the derived stage so legacy projects don't look out of
// place next to projects that have a real phase saved.

function PhaseCell({
  projectId,
  phase,
  fallbackStage,
  canEdit,
}: {
  projectId: string;
  phase: ProjectPhase | null;
  fallbackStage: StageKey;
  canEdit: boolean;
}) {
  // Pick the colour class — actual phase → stable colour per phase;
  // no phase → use the derived stage so legacy rows look familiar.
  const stageClass: StageKey = phase
    ? phaseToStageClass(phase)
    : fallbackStage;

  const chipText = phaseLabel(phase);

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        cursor: canEdit ? 'pointer' : 'default',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <span className={`stage-pill stage-${stageClass}`}>{chipText}</span>
      {canEdit ? (
        <select
          value={phase ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            const next = (v as ProjectPhase) || null;
            updateProject({
              projectId,
              phase: next,
            }).catch((err) => {
              console.warn('[phase update] failed', err);
            });
          }}
          aria-label="Change project phase"
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            cursor: 'pointer',
            width: '100%',
            height: '100%',
            border: 0,
            // Safari: zero out the default <select> appearance so
            // the invisible overlay accepts pointer events evenly.
            appearance: 'none',
            WebkitAppearance: 'none',
            background: 'transparent',
          }}
        >
          <option value="" disabled>
            Set phase
          </option>
          {PROJECT_PHASES.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
      ) : null}
    </span>
  );
}

/** Map a real `ProjectPhase` value to the existing iio.css
 *  `.stage-{class}` colour modifier — so the chip stays
 *  visually consistent with the rest of the table. */
function phaseToStageClass(p: ProjectPhase): StageKey {
  switch (p) {
    case 'design':     return 'design';
    case 'drawing_2d': return 'design';     // same family — early-stage blue
    case 'render_3d':  return 'approval';   // amber/orange — review-style
    case 'execution':  return 'execution';
    case 'handover':   return 'handover';
  }
}
